import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../services/api';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import {
  Plus,
  ArrowLeft,
  Loader2,
  Play,
  Users,
  MessageSquare,
  Calendar,
  Trash2,
  Pencil,
  RefreshCw,
  Gift,
  ChevronRight,
  Repeat
} from 'lucide-react';
import FlowDayBadges from '../../components/FlowDayBadges';
import FixedFlowModal from '../../components/FixedFlowModal';

const WAFixedFlowsPage = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('fixed');
  const [flows, setFlows] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingFlow, setEditingFlow] = useState(null);
  const [saving, setSaving] = useState(false);
  const [summaries, setSummaries] = useState({});

  // Anniversary state
  const [anniversaryFlows, setAnniversaryFlows] = useState([]);
  const [showAnniversaryModal, setShowAnniversaryModal] = useState(false);
  const [editingAnniversary, setEditingAnniversary] = useState(null);
  const [savingAnniversary, setSavingAnniversary] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [flowsRes, templatesRes, annRes] = await Promise.all([
        api.get('/wa/fixed-flows'),
        api.get('/wa/templates'),
        api.get('/wa/anniversary-flows')
      ]);
      setFlows(flowsRes.data);
      setTemplates(templatesRes.data);
      setAnniversaryFlows(annRes.data);
      // Fetch summaries for each flow
      const sums = {};
      await Promise.all(flowsRes.data.map(async (f) => {
        try {
          const res = await api.get(`/wa/fixed-flows/${f.id}/summary`);
          sums[f.id] = res.data;
        } catch {}
      }));
      setSummaries(sums);
    } catch (error) {
    } finally {
      setLoading(false);
    }
  };

  const openCreateModal = () => {
    setEditingFlow(null);
    setShowModal(true);
  };

  const openEditModal = (flow) => {
    setEditingFlow(flow);
    setShowModal(true);
  };

  const handleSubmit = async (payload, editing) => {
    setSaving(true);
    try {
      if (editing) {
        await api.put(`/wa/fixed-flows/${editing.id}`, payload);
      } else {
        await api.post('/wa/fixed-flows', payload);
      }
      setShowModal(false);
      fetchData();
    } catch (error) {
      alert('Error saving flow. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (flowId) => {
    if (!window.confirm('Are you sure you want to delete this flow? All client entries will be removed.')) {
      return;
    }
    
    try {
      await api.delete(`/wa/fixed-flows/${flowId}`);
      fetchData();
    } catch (error) {
    }
  };

  const handleAutoEnroll = async () => {
    try {
      const response = await api.post('/wa/fixed-flows/auto-enroll');
      alert(`Auto-enrolled ${response.data.enrolled} new clients`);
      fetchData();
    } catch (error) {
    }
  };

  const handleCreateAnniversary = async (payload, editing) => {
    setSavingAnniversary(true);
    try {
      if (editing) {
        await api.put(`/wa/anniversary-flows/${editing.id}`, payload);
      } else {
        await api.post('/wa/anniversary-flows', payload);
      }
      setShowAnniversaryModal(false);
      setEditingAnniversary(null);
      fetchData();
    } catch (e) {
      alert(e.response?.data?.detail || 'Error saving flow');
    }
    setSavingAnniversary(false);
  };

  const handleDeleteAnniversary = async (id, name) => {
    if (!window.confirm(`Delete anniversary flow "${name}"? This removes all entries.`)) return;
    try {
      await api.delete(`/wa/anniversary-flows/${id}`);
      fetchData();
    } catch {}
  };

  // Render flow cards
  const renderFlowCards = () => {
    const cards = [];
    for (let i = 0; i < flows.length; i++) {
      const flow = flows[i];
      cards.push(
        <Card key={flow.id} className="p-6 bg-white border border-gray-100">
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <div className="flex items-center gap-3 mb-2">
                <h3 className="text-lg font-semibold text-gray-900">{flow.name}</h3>
                <span className={`px-2 py-1 text-xs rounded-full ${
                  flow.is_active 
                    ? 'bg-green-100 text-green-700' 
                    : 'bg-gray-100 text-gray-600'
                }`}>
                  {flow.is_active ? 'Active' : 'Inactive'}
                </span>
                {flow.auto_enroll_new_clients && (
                  <span className="px-2 py-1 text-xs rounded-full bg-blue-100 text-blue-700">
                    Auto-Enroll
                  </span>
                )}
              </div>
              {flow.description && (
                <p className="text-gray-500 text-sm mb-3">{flow.description}</p>
              )}
              
              {/* Stats */}
              <div className="flex items-center gap-6 text-sm">
                <div className="flex items-center gap-2">
                  <Users size={16} className="text-gray-400" />
                  <span className="text-gray-600">
                    <strong>{flow.stats?.total_clients || 0}</strong> clients
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-green-600">
                    {flow.stats?.active || 0} active
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-purple-600">
                    {flow.stats?.completed || 0} completed
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-gray-500">
                    {flow.stats?.stopped || 0} stopped
                  </span>
                </div>
              </div>
              
              {/* Summary Stats */}
              {summaries[flow.id] && (
                <div className="flex items-center gap-4 mt-2 text-xs">
                  <span className="px-2 py-1 rounded bg-green-50 text-green-700 font-medium">
                    {summaries[flow.id].sent_today} sent today
                  </span>
                  <span className="px-2 py-1 rounded bg-blue-50 text-blue-700 font-medium">
                    {summaries[flow.id].sent_this_week} this week
                  </span>
                  <span className="px-2 py-1 rounded bg-purple-50 text-purple-700 font-medium">
                    {summaries[flow.id].sent_this_month} this month
                  </span>
                  {summaries[flow.id].repurchased_count > 0 && (
                    <span className="px-2 py-1 rounded bg-emerald-50 text-emerald-700 font-medium">
                      {summaries[flow.id].repurchased_count} repurchased ({summaries[flow.id].repurchase_rate}%)
                    </span>
                  )}
                </div>
              )}

              {/* Day Templates */}
              <FlowDayBadges dayTemplates={flow.day_templates} />
            </div>
            
            {/* Actions */}
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => navigate(`/whatsapp/fixed-flows/${flow.id}`)}
                className="text-green-600 border-green-200 hover:bg-green-50"
              >
                <Play size={14} className="mr-1" />
                View Queue
              </Button>
              <button 
                onClick={() => openEditModal(flow)}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <Pencil size={16} className="text-gray-500" />
              </button>
              <button 
                onClick={() => handleDelete(flow.id)}
                className="p-2 hover:bg-red-50 rounded-lg transition-colors"
              >
                <Trash2 size={16} className="text-red-500" />
              </button>
            </div>
          </div>
        </Card>
      );
    }
    return cards;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="animate-spin text-[#4F46E5]" size={32} />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button 
            variant="outline" 
            size="sm"
            onClick={() => navigate('/whatsapp')}
          >
            <ArrowLeft size={16} className="mr-2" />
            Back
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Flow Campaigns</h1>
            <p className="text-gray-500 mt-1">Automated message sequences</p>
          </div>
        </div>
        <div className="flex gap-2">
          {activeTab === 'fixed' && (
            <>
              <Button 
                variant="outline"
                onClick={handleAutoEnroll}
              >
                <RefreshCw size={16} className="mr-2" />
                Auto-Enroll New Clients
              </Button>
              <Button 
                onClick={openCreateModal}
                className="bg-[#4F46E5] hover:bg-[#4338CA] text-white"
              >
                <Plus size={16} className="mr-2" />
                Create Flow
              </Button>
            </>
          )}
          {activeTab === 'anniversary' && (
            <Button 
              onClick={() => navigate('/whatsapp/anniversary-flows/create')}
              className="bg-[#4F46E5] hover:bg-[#4338CA] text-white"
              data-testid="create-anniversary-btn"
            >
              <Plus size={16} className="mr-2" />
              New Anniversary Flow
            </Button>
          )}
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 border-b border-gray-200">
        <button
          onClick={() => setActiveTab('fixed')}
          className={`px-5 py-2.5 text-sm font-medium border-b-2 transition-colors flex items-center gap-2 ${
            activeTab === 'fixed' ? 'border-[#4F46E5] text-[#4F46E5]' : 'border-transparent text-gray-500 hover:text-gray-700'
          }`}
          data-testid="tab-fixed-flows"
        >
          <Repeat size={15} /> Fixed Flows
        </button>
        <button
          onClick={() => setActiveTab('anniversary')}
          className={`px-5 py-2.5 text-sm font-medium border-b-2 transition-colors flex items-center gap-2 ${
            activeTab === 'anniversary' ? 'border-[#4F46E5] text-[#4F46E5]' : 'border-transparent text-gray-500 hover:text-gray-700'
          }`}
          data-testid="tab-anniversary-flows"
        >
          <Gift size={15} /> Anniversary Flows
        </button>
      </div>

      {/* Fixed Flows Tab */}
      {activeTab === 'fixed' && (
        <>
          {/* Info Card */}
          <Card className="p-4 bg-blue-50 border-blue-200">
            <div className="flex items-start gap-3">
              <Calendar className="text-blue-600 mt-0.5" size={20} />
              <div>
                <p className="font-medium text-blue-900">How Fixed Flows Work</p>
                <p className="text-sm text-blue-700 mt-1">
                  New clients automatically join active flows. You can customize which days to send messages (e.g., Day 1, 3, 7, 14, etc.).
                  Each client follows their own timeline based on when they were enrolled.
                </p>
              </div>
            </div>
          </Card>

          {/* Flows List */}
          {flows.length === 0 ? (
            <Card className="p-12 text-center bg-white border border-gray-100">
              <MessageSquare className="mx-auto text-gray-300 mb-4" size={48} />
              <h3 className="text-lg font-medium text-gray-900 mb-2">No Fixed Flows Yet</h3>
              <p className="text-gray-500 mb-4">Create your first fixed flow campaign to automate client follow-ups</p>
              <Button 
                onClick={openCreateModal}
                className="bg-[#4F46E5] hover:bg-[#4338CA] text-white"
              >
                <Plus size={16} className="mr-2" />
                Create Flow
              </Button>
            </Card>
          ) : (
            <div className="grid gap-4">
              {renderFlowCards()}
            </div>
          )}
        </>
      )}

      {/* Anniversary Flows Tab */}
      {activeTab === 'anniversary' && (
        <>
          <Card className="p-4 bg-purple-50 border-purple-200">
            <div className="flex items-start gap-3">
              <Gift className="text-purple-600 mt-0.5" size={20} />
              <div>
                <p className="font-medium text-purple-900">How Anniversary Flows Work</p>
                <p className="text-sm text-purple-700 mt-1">
                  Send yearly messages to clients on their purchase anniversary. Set up Year 1, 2, 3 templates — each client gets the right message on the anniversary of their first order.
                </p>
              </div>
            </div>
          </Card>

          {anniversaryFlows.length === 0 ? (
            <Card className="p-12 text-center bg-white border border-gray-100">
              <Gift size={48} className="mx-auto text-gray-300 mb-4" />
              <h3 className="text-lg font-medium text-gray-700">No Anniversary Flows</h3>
              <p className="text-gray-400 text-sm mt-1 mb-4">Create one to send yearly messages to your clients on their purchase anniversary.</p>
              <Button onClick={() => { setEditingAnniversary(null); setShowAnniversaryModal(true); }} className="bg-[#4F46E5] hover:bg-[#4338CA] text-white">
                <Plus size={16} className="mr-2" /> New Anniversary Flow
              </Button>
            </Card>
          ) : (
            <div className="grid gap-4">
              {anniversaryFlows.map(flow => (
                <Card key={flow.id} className="bg-white border border-gray-100 hover:border-[#4F46E5]/30 transition-colors cursor-pointer" onClick={() => navigate(`/whatsapp/anniversary-flows/${flow.id}`)}>
                  <div className="p-5 flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#4F46E5]/10 to-purple-100 flex items-center justify-center">
                        <Gift className="text-[#4F46E5]" size={24} />
                      </div>
                      <div>
                        <h3 className="font-semibold text-gray-900">{flow.name}</h3>
                        <div className="flex items-center gap-3 mt-1 text-sm text-gray-500">
                          <span className="flex items-center gap-1"><Calendar size={13} /> {flow.year_templates?.length || 0} year templates</span>
                          <span className="flex items-center gap-1"><Users size={13} /> {flow.stats?.total_clients || 0} clients</span>
                          <Badge variant={flow.is_active ? "default" : "secondary"} className={flow.is_active ? "bg-green-100 text-green-700" : ""}>
                            {flow.is_active ? 'Active' : 'Inactive'}
                          </Badge>
                        </div>
                        {flow.year_templates?.length > 0 && (
                          <div className="flex flex-wrap gap-1.5 mt-2">
                            {flow.year_templates.map(yt => (
                              <span key={yt.year} className="px-2 py-0.5 text-xs bg-purple-50 text-purple-700 rounded border border-purple-200">Year {yt.year}</span>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-gray-400">
                        {flow.stats?.active || 0} active, {flow.stats?.completed || 0} completed
                      </span>
                      <Button variant="outline" size="sm" onClick={(e) => { e.stopPropagation(); navigate(`/whatsapp/anniversary-flows/${flow.id}`); }} className="text-green-600 border-green-200 hover:bg-green-50">
                        <Play size={14} className="mr-1" /> View Queue
                      </Button>
                      <button onClick={(e) => { e.stopPropagation(); handleDeleteAnniversary(flow.id, flow.name); }} className="p-2 hover:bg-red-50 rounded-lg transition-colors">
                        <Trash2 size={16} className="text-red-500" />
                      </button>
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          )}
        </>
      )}

      {/* Create/Edit Fixed Flow Modal */}
      <FixedFlowModal
        open={showModal}
        onClose={setShowModal}
        editingFlow={editingFlow}
        templates={templates}
        onSubmit={handleSubmit}
        saving={saving}
      />
    </div>
  );
};

export default WAFixedFlowsPage;

function AnniversaryFormInline({ editingFlow, templates, saving, onSubmit, onCancel }) {
  const DEFAULT_YEARS = [1, 2, 3];
  const [name, setName] = React.useState(editingFlow?.name || '');
  const [description, setDescription] = React.useState(editingFlow?.description || '');
  const [isActive, setIsActive] = React.useState(editingFlow?.is_active !== false);
  const [yearTemplates, setYearTemplates] = React.useState(() => {
    const yt = editingFlow?.year_templates;
    return (yt && yt.length > 0) ? yt.map(y => ({ year: y.year, template_id: y.template_id || '' })) : DEFAULT_YEARS.map(y => ({ year: y, template_id: '' }));
  });
  const [newYearInput, setNewYearInput] = React.useState('');

  const addYear = () => {
    const y = parseInt(newYearInput);
    if (isNaN(y) || y < 1) return;
    if (yearTemplates.some(yt => yt.year === y)) return;
    setYearTemplates(prev => [...prev, { year: y, template_id: '' }].sort((a, b) => a.year - b.year));
    setNewYearInput('');
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const filtered = yearTemplates.filter(yt => yt.template_id);
    if (filtered.length === 0) return alert('Select at least one template');
    onSubmit({ name, description, is_active: isActive, year_templates: filtered }, editingFlow);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-5">
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Flow Name *</label>
        <Input placeholder="e.g., Purchase Anniversary" value={name} onChange={(e) => setName(e.target.value)} required />
      </div>
      <div className="flex items-center gap-2">
        <input type="checkbox" checked={isActive} onChange={(e) => setIsActive(e.target.checked)} className="rounded border-gray-300" />
        <span className="text-sm text-gray-700">Active</span>
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-3">Message Schedule (Years)</label>
        <div className="flex items-center gap-2 mb-4 bg-gray-50 p-3 rounded-lg">
          <span className="text-sm text-gray-600">Add Year:</span>
          <Input type="number" min="1" max="50" placeholder="e.g., 4" value={newYearInput} onChange={(e) => setNewYearInput(e.target.value)} className="w-24" onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addYear(); } }} />
          <Button type="button" variant="outline" size="sm" onClick={addYear}><Plus size={14} className="mr-1" /> Add</Button>
        </div>
        <div className="space-y-3">
          {yearTemplates.map((yt) => (
            <div key={yt.year} className="flex items-center gap-3">
              <span className="text-sm font-bold text-[#4F46E5] w-16">Year {yt.year}</span>
              <select className="flex-1 border border-gray-200 rounded-lg px-3 py-2 text-sm" value={yt.template_id} onChange={(e) => setYearTemplates(prev => prev.map(y => y.year === yt.year ? { ...y, template_id: e.target.value } : y))}>
                <option value="">None</option>
                {templates.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
              </select>
              <button type="button" onClick={() => setYearTemplates(prev => prev.filter(y => y.year !== yt.year))} className="text-red-400 hover:text-red-600 p-1"><X size={16} /></button>
            </div>
          ))}
        </div>
      </div>
      <div className="flex gap-2 justify-end pt-2">
        <Button type="button" variant="outline" onClick={onCancel}>Cancel</Button>
        <Button type="submit" disabled={saving || !name} className="bg-[#4F46E5] hover:bg-[#4338CA] text-white">
          {saving && <Loader2 className="animate-spin mr-2" size={16} />}
          {editingFlow ? 'Update Flow' : 'Create Flow'}
        </Button>
      </div>
    </form>
  );
}
